import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const btn = "border border-[var(--phosphor)] px-2 py-1 hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40";
const field = "bg-black border border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] text-[var(--phosphor)] px-2 py-1 outline-none";

export default function MegaSyncPanel({
  apiBase,
  authHeaders,
  channels,
  selectedChannel,
  append,
  onUploaded,
}) {
  const [open] = useState(true);
  const [status, setStatus] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewItems, setReviewItems] = useState([]);
  const [reviewBusyKey, setReviewBusyKey] = useState("");
  const [reviewCandidates, setReviewCandidates] = useState({});
  const [reviewQueries, setReviewQueries] = useState({});
  const [busy, setBusy] = useState(false);
  const [ingestFile, setIngestFile] = useState(null);
  const [ingestChannelId, setIngestChannelId] = useState(selectedChannel || "");
  const [ingestProgress, setIngestProgress] = useState(0);
  const [ingestStatus, setIngestStatus] = useState("");
  const [ingestResult, setIngestResult] = useState(null);
  const [ingestBusy, setIngestBusy] = useState(false);
  const [manageChannelId, setManageChannelId] = useState(selectedChannel || "");
  const [manageRows, setManageRows] = useState([]);
  const [manageVideoId, setManageVideoId] = useState("");
  const [manageBusy, setManageBusy] = useState(false);
  const [manageRename, setManageRename] = useState("");
  const [manageMoveChannelId, setManageMoveChannelId] = useState("");
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceProgress, setReplaceProgress] = useState(0);
  const [manageStatus, setManageStatus] = useState("");

  useEffect(() => {
    if (selectedChannel) {
    }
  }, [selectedChannel]);

  const realChannels = useMemo(
    () => channels.filter((c) => !c.isSubChannel),
    [channels]
  );

  const load = useCallback(async () => {
    try {
      const [s, m, d] = await Promise.all([
        axios.get(`${apiBase}/mega-sync/status`, { headers: authHeaders() }),
        axios.get(`${apiBase}/mega-sync/mappings`, { headers: authHeaders() }),
        axios.get(`${apiBase}/mega-sync/dashboard`, { headers: authHeaders() }),
      ]);
      setStatus(s.data);
      setMappings(m.data?.mappings || []);
      setDashboard(d.data || null);
    } catch (e) {
      append(`> MEGA SYNC ERROR: ${e.response?.data?.detail || e.message}`);
    }
  }, [apiBase, authHeaders, append]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const activeS4Mappings = useMemo(
    () => mappings
      .filter((m) => m.storage_provider === "mega_s4" && m.enabled)
      .sort((a, b) => Number(a.channel?.number || 9999) - Number(b.channel?.number || 9999)),
    [mappings]
  );

  // Movie tools default to the first production Cinema mapping in numeric channel order.
  // Once the operator chooses another valid Cinema channel, preserve that choice.
  useEffect(() => {
    const firstChannelId = activeS4Mappings[0]?.channel_id || "";
    if (!firstChannelId) return;
    const valid = (id) => activeS4Mappings.some((m) => m.channel_id === id);
    setIngestChannelId((current) => valid(current) ? current : firstChannelId);
    setManageChannelId((current) => valid(current) ? current : firstChannelId);
  }, [activeS4Mappings]);

  const ingestMapping = useMemo(
    () => activeS4Mappings.find((m) => m.channel_id === ingestChannelId) || null,
    [activeS4Mappings, ingestChannelId]
  );

  const uploadMovieToS4 = async () => {
    if (!ingestFile || !ingestChannelId) {
      append("> S4 INGEST: CHOOSE A MOVIE FILE AND DESTINATION CHANNEL");
      return;
    }

    setIngestBusy(true);
    setIngestProgress(0);
    setIngestResult(null);
    setIngestStatus("PREPARING");
    append(`> S4 INGEST PRECHECK — ${ingestFile.name}`);

    try {
      const { data: prep } = await axios.post(
        `${apiBase}/mega-sync/s4-ingest/prepare`,
        {
          channel_id: ingestChannelId,
          filename: ingestFile.name,
          size: ingestFile.size,
          content_type: ingestFile.type || "application/octet-stream",
        },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );

      const tmdbHint = prep.tmdb_preflight?.status === "matched"
        ? ` — TMDB ${prep.tmdb_preflight.tmdb_id} MATCHED`
        : prep.tmdb_preflight?.status === "review"
          ? " — TMDB REVIEW EXPECTED"
          : "";

      append(`> S4 DESTINATION READY — ${prep.object_key}${tmdbHint}`);
      setIngestStatus("UPLOADING DIRECTLY TO S4");

      // Do not proxy movie bytes through scartlead.tv. The short-lived signed
      // URL sends the file directly from the browser to private S4 storage.
      await axios.put(prep.upload_url, ingestFile, {
        headers: ingestFile.type ? { "Content-Type": ingestFile.type } : {},
        onUploadProgress: (evt) => {
          const total = evt.total || ingestFile.size || 1;
          const pct = Math.max(0, Math.min(100, Math.round((evt.loaded / total) * 100)));
          setIngestProgress(pct);
        },
      });

      setIngestProgress(100);
      setIngestStatus("VERIFYING + ADDING TO CATALOGUE");
      append("> S4 UPLOAD COMPLETE — VERIFYING OBJECT + RUNNING MOVIE SYNC");

      const { data: finalised } = await axios.post(
        `${apiBase}/mega-sync/s4-ingest/finalize`,
        {
          mapping_id: prep.mapping_id,
          object_key: prep.object_key,
          expected_size: prep.expected_size,
        },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );

      setIngestResult(finalised);
      setIngestStatus("COMPLETE");

      const tape = finalised.tape || {};
      const tmdbText = tape.tmdb_id
        ? `TMDB ${tape.tmdb_id}`
        : tape.tmdb_status === "review"
          ? "TMDB REVIEW"
          : "TMDB UNMATCHED";

      append(`> S4 MOVIE ADDED — ${tape.title || prep.title} — ${tmdbText}`);
      await load();
      if (onUploaded) await onUploaded(ingestChannelId);
      setIngestFile(null);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setIngestStatus("FAILED");
      append(`> S4 INGEST ERROR: ${detail}`);
    } finally {
      setIngestBusy(false);
    }
  };

  const loadS4Library = useCallback(async (channelId = manageChannelId) => {
    if (!channelId) {
      setManageRows([]);
      setManageVideoId("");
      return;
    }
    try {
      const { data } = await axios.get(`${apiBase}/mega-sync/s4-library`, {
        params: { channel_id: channelId },
        headers: authHeaders(),
      });
      const rows = data.movies || [];
      setManageRows(rows);
      setManageVideoId((current) =>
        rows.some((row) => row.id === current) ? current : (rows[0]?.id || "")
      );
    } catch (e) {
      append(`> S4 LIBRARY ERROR: ${e.response?.data?.detail || e.message}`);
    }
  }, [apiBase, authHeaders, append, manageChannelId]);

  useEffect(() => {
    if (open && manageChannelId) loadS4Library(manageChannelId);
  }, [open, manageChannelId, loadS4Library]);

  const manageMovie = useMemo(
    () => manageRows.find((row) => row.id === manageVideoId) || null,
    [manageRows, manageVideoId]
  );

  useEffect(() => {
    if (!manageMovie) {
      setManageRename("");
      setReplaceFile(null);
      setReplaceProgress(0);
      return;
    }
    const currentName = (manageMovie.s4_object_key || manageMovie.mega_sync_source_path || "").split("/").pop() || "";
    setManageRename(currentName);
    setReplaceFile(null);
    setReplaceProgress(0);
    setManageStatus("");
  }, [manageMovie]);

  const refreshAfterManagement = async (...channelIds) => {
    await load();
    await loadS4Library(manageChannelId);
    if (onUploaded) {
      for (const id of [...new Set(channelIds.filter(Boolean))]) {
        await onUploaded(id);
      }
    }
  };

  const renameManagedMovie = async () => {
    if (!manageMovie || !manageRename.trim()) return;
    setManageBusy(true);
    setManageStatus("RENAMING");
    append(`> S4 RENAME — ${manageMovie.title}`);
    try {
      const { data } = await axios.post(
        `${apiBase}/mega-sync/s4-manage/rename`,
        { video_id: manageMovie.id, filename: manageRename.trim() },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      append(`> S4 RENAME COMPLETE — ${data.old_title} → ${data.title}`);
      setManageStatus("RENAME COMPLETE");
      await refreshAfterManagement(manageMovie.channel_id || manageChannelId);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      append(`> S4 RENAME ERROR: ${detail}`);
      setManageStatus("RENAME FAILED");
    } finally {
      setManageBusy(false);
    }
  };

  const moveManagedMovie = async () => {
    if (!manageMovie || !manageMoveChannelId) return;
    const destination = activeS4Mappings.find((m) => m.channel_id === manageMoveChannelId);
    const ch = destination?.channel || realChannels.find((c) => c.id === manageMoveChannelId);
    if (!window.confirm(`Move "${manageMovie.title}" to ${ch ? `CH ${ch.number} — ${ch.name}` : "the selected channel"}?`)) return;

    setManageBusy(true);
    setManageStatus("MOVING");
    append(`> S4 MOVE — ${manageMovie.title}`);
    const sourceChannel = manageChannelId;
    try {
      const { data } = await axios.post(
        `${apiBase}/mega-sync/s4-manage/move`,
        { video_id: manageMovie.id, destination_channel_id: manageMoveChannelId },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      append(`> S4 MOVE COMPLETE — ${data.title} → CH ${data.to?.channel?.number}`);
      setManageStatus("MOVE COMPLETE");
      setManageMoveChannelId("");
      await refreshAfterManagement(sourceChannel, manageMoveChannelId);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      append(`> S4 MOVE ERROR: ${detail}`);
      setManageStatus("MOVE FAILED");
    } finally {
      setManageBusy(false);
    }
  };

  const deleteManagedMovie = async () => {
    if (!manageMovie) return;
    if (!window.confirm(
      `PERMANENTLY remove "${manageMovie.title}"?\n\nThis deletes the physical S4 movie object and removes the tape from the catalogue.`
    )) return;

    setManageBusy(true);
    setManageStatus("DELETING");
    append(`> S4 DELETE — ${manageMovie.title}`);
    try {
      const { data } = await axios.post(
        `${apiBase}/mega-sync/s4-manage/delete`,
        { video_id: manageMovie.id },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      append(`> S4 DELETE COMPLETE — ${data.title}`);
      setManageStatus("DELETE COMPLETE");
      await refreshAfterManagement(manageChannelId);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      append(`> S4 DELETE ERROR: ${detail}`);
      setManageStatus("DELETE FAILED");
    } finally {
      setManageBusy(false);
    }
  };

  const replaceManagedMovie = async () => {
    if (!manageMovie || !replaceFile) return;
    if (!window.confirm(
      `Replace the physical S4 file for "${manageMovie.title}" with "${replaceFile.name}"?\n\nMetadata and playlist position will be preserved.`
    )) return;

    setManageBusy(true);
    setReplaceProgress(0);
    setManageStatus("PREPARING REPLACEMENT");
    append(`> S4 REPLACE PRECHECK — ${manageMovie.title}`);

    try {
      const { data: prep } = await axios.post(
        `${apiBase}/mega-sync/s4-manage/replace/prepare`,
        {
          video_id: manageMovie.id,
          filename: replaceFile.name,
          size: replaceFile.size,
          content_type: replaceFile.type || "application/octet-stream",
        },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );

      setManageStatus("UPLOADING REPLACEMENT TO S4");
      await axios.put(prep.upload_url, replaceFile, {
        headers: replaceFile.type ? { "Content-Type": replaceFile.type } : {},
        onUploadProgress: (evt) => {
          const total = evt.total || replaceFile.size || 1;
          setReplaceProgress(Math.max(0, Math.min(100, Math.round((evt.loaded / total) * 100))));
        },
      });

      setReplaceProgress(100);
      setManageStatus("VERIFYING + PROMOTING REPLACEMENT");

      const { data } = await axios.post(
        `${apiBase}/mega-sync/s4-manage/replace/finalize`,
        {
          video_id: manageMovie.id,
          temp_object_key: prep.temp_object_key,
          expected_size: prep.expected_size,
        },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );

      append(`> S4 REPLACE COMPLETE — ${data.title} — ${data.new_size} BYTES`);
      setManageStatus("REPLACE COMPLETE");
      setReplaceFile(null);
      await refreshAfterManagement(manageChannelId);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      append(`> S4 REPLACE ERROR: ${detail}`);
      setManageStatus("REPLACE FAILED");
    } finally {
      setManageBusy(false);
    }
  };

  const reviewKey = (item) => `${item.mapping_id}::${item.remote_path}`;

  const loadReviewQueue = async (forceOpen = true) => {
    try {
      const { data } = await axios.get(`${apiBase}/mega-sync/tmdb-review`, {
        headers: authHeaders(),
      });
      setReviewItems(data.items || []);
      if (forceOpen) setReviewOpen(true);
    } catch (e) {
      append(`> TMDB REVIEW ERROR: ${e.response?.data?.detail || e.message}`);
    }
  };

  const searchReviewCandidates = async (item) => {
    const key = reviewKey(item);
    setReviewBusyKey(key);
    try {
      const { data } = await axios.get(`${apiBase}/mega-sync/tmdb-review/candidates`, {
        params: {
          mapping_id: item.mapping_id,
          remote_path: item.remote_path,
          q: reviewQueries[key] || item.video_title || item.video?.title || "",
        },
        headers: authHeaders(),
      });
      setReviewCandidates((prev) => ({ ...prev, [key]: data.candidates || [] }));
      setReviewQueries((prev) => ({ ...prev, [key]: data.query || prev[key] || "" }));
    } catch (e) {
      append(`> TMDB CANDIDATE ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setReviewBusyKey("");
    }
  };

  const applyReviewCandidate = async (item, candidate) => {
    const key = reviewKey(item);
    setReviewBusyKey(key);
    try {
      await axios.post(
        `${apiBase}/mega-sync/tmdb-review/apply`,
        {
          mapping_id: item.mapping_id,
          remote_path: item.remote_path,
          tmdb_id: candidate.tmdb_id,
          media_type: candidate.media_type,
        },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      append(`> TMDB REVIEW APPLIED — ${candidate.title}${candidate.year ? ` (${candidate.year})` : ""}`);
      setReviewCandidates((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      await loadReviewQueue(false);
      await load();
      if (item.video?.channel_id && onUploaded) await onUploaded(item.video.channel_id);
    } catch (e) {
      append(`> TMDB REVIEW APPLY ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setReviewBusyKey("");
    }
  };

  const dismissReviewItem = async (item) => {
    if (!window.confirm(`Leave "${item.video_title || item.video?.title || "this tape"}" unmatched?`)) return;
    const key = reviewKey(item);
    setReviewBusyKey(key);
    try {
      await axios.post(
        `${apiBase}/mega-sync/tmdb-review/dismiss`,
        {
          mapping_id: item.mapping_id,
          remote_path: item.remote_path,
        },
        { headers: { "Content-Type": "application/json", ...authHeaders() } }
      );
      append(`> TMDB REVIEW DISMISSED — TAPE LEFT UNMATCHED`);
      await loadReviewQueue(false);
      await load();
    } catch (e) {
      append(`> TMDB REVIEW DISMISS ERROR: ${e.response?.data?.detail || e.message}`);
    } finally {
      setReviewBusyKey("");
    }
  };

  const dashDate = (value) => (
    value ? new Date(value).toLocaleString() : "NEVER"
  );

  const healthLabel = dashboard?.health === "healthy"
    ? "ALL SYSTEMS OK"
    : dashboard?.health === "attention"
      ? "ATTENTION"
      : dashboard?.health === "warming_up"
        ? "INITIALISING"
        : "IDLE";

  const healthHint = dashboard?.health === "healthy"
    ? "ALL ENABLED MAPS ARE SCANNING WITHOUT CURRENT ERRORS"
    : dashboard?.health === "attention"
      ? `${dashboard?.problem_mapping_count || 0} MAP(S) REPORT AN ERROR`
      : dashboard?.health === "warming_up"
        ? `${dashboard?.never_scanned_count || 0} ENABLED MAP(S) HAVE NOT COMPLETED A SCAN YET`
        : "NO ENABLED FOLDER MAPPINGS";

  const statusText = !status
    ? "CHECKING..."
    : !status.installed
      ? "LEGACY MEGA TOOLS UNAVAILABLE"
      : status.logged_in
        ? `READY${status.account ? ` — ${status.account}` : ""}`
        : "LEGACY MEGA LOGIN REQUIRED";

  return (
    <div className="border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-2" data-testid="mega-sync-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] tracking-widest" data-testid="mega-sync-heading">
          MOVIES — ADD / MANAGE / REVIEW
        </div>
        {open && (
          <span className={`text-[9px] tracking-widest ${status?.logged_in ? "" : "opacity-60"}`}>
            {statusText}
          </span>
        )}
      </div>

      {open && (
        <div className="mt-2 space-y-3">
          <div className="text-[9px] opacity-70 leading-relaxed">
            AUTOMATIC S4 INGEST WATCHES CINEMA FOLDERS ABOUT EVERY 5 MINUTES. MAPPING, TMDB AND DURATION COMPLETION ARE NORMALLY AUTOMATIC.
            <br />
            NEW VIDEO FILES ARE ADDED; EXISTING TAPES ARE NEVER DELETED AUTOMATICALLY.
          </div>

          {dashboard && (
            <div
              className="border border-[color-mix(in_srgb,var(--phosphor)_38%,transparent)] p-2"
              data-testid="mega-sync-dashboard"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-widest">MOVIE INGEST HEALTH</div>
                  <div className="mt-1 text-[9px] opacity-65">{healthHint}</div>
                </div>
                <div className="text-[10px] tracking-widest">
                  ● {healthLabel}
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
                <div className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                  <div className="text-[8px] opacity-55">MAPS ON</div>
                  <div className="text-[15px]">{dashboard.enabled_mapping_count || 0}/{dashboard.mapping_count || 0}</div>
                </div>
                <div className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                  <div className="text-[8px] opacity-55">FILES WATCHED</div>
                  <div className="text-[15px]">{dashboard.files_watched || 0}</div>
                </div>
                <div className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                  <div className="text-[8px] opacity-55">SYNCED TAPES</div>
                  <div className="text-[15px]">{dashboard.synced_tapes || 0}</div>
                </div>
                <div className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                  <div className="text-[8px] opacity-55">NEW ADDED</div>
                  <div className="text-[15px]">{dashboard.lifetime_added || 0}</div>
                </div>
                <div className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                  <div className="text-[8px] opacity-55">TMDB MATCHED</div>
                  <div className="text-[15px]">{dashboard.lifetime_tmdb_matched || 0}</div>
                </div>
                <button
                  type="button"
                  onClick={() => loadReviewQueue(true)}
                  className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2 text-left hover:bg-[color-mix(in_srgb,var(--phosphor)_8%,transparent)] transition-colors"
                  title="Open TMDb review queue"
                >
                  <div className="text-[8px] opacity-55">TMDB REVIEW</div>
                  <div className="text-[15px]">{dashboard.pending_tmdb_review || 0}</div>
                  <div className="text-[7px] opacity-45">OPEN QUEUE</div>
                </button>
                <div className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                  <div className="text-[8px] opacity-55">FAILURES</div>
                  <div className="text-[15px]">{dashboard.latest_failure_count || 0}</div>
                </div>
                <div className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-2">
                  <div className="text-[8px] opacity-55">TOTAL SCANS</div>
                  <div className="text-[15px]">{dashboard.lifetime_scans || 0}</div>
                </div>
              </div>

              <div className="mt-2 flex gap-x-5 gap-y-1 flex-wrap text-[8px] opacity-60">
                <span>LAST SCAN: {dashDate(dashboard.last_scan_at)}</span>
                <span>LAST SUCCESS: {dashDate(dashboard.last_success_at)}</span>
                <span>PROBLEM MAPS: {dashboard.problem_mapping_count || 0}</span>
              </div>
            </div>
          )}

          <div
            className="border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] p-3"
            data-testid="s4-movie-ingest"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] tracking-[0.16em] font-bold">ADD MOVIE TO S4</div>
                <div className="mt-1 text-[9px] opacity-65">
                  DIRECT BROWSER → S4 UPLOAD. AFTER VERIFICATION SCARTLEAD.TV ADDS THE FILM TO THE CATALOGUE AND RUNS TMDB.
                </div>
              </div>
              <div className="text-[9px] opacity-60">
                {activeS4Mappings.length} S4 CHANNEL{activeS4Mappings.length === 1 ? "" : "S"} READY
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 lg:grid-cols-[minmax(240px,1fr)_minmax(220px,0.8fr)_auto] gap-2 items-end">
              <label className="text-[9px]">
                <span className="block mb-1 opacity-65">MOVIE FILE</span>
                <input
                  type="file"
                  accept=".mp4,.mkv,.avi,.mov,.webm,.m4v,.wmv,.flv,.mpg,.mpeg,.ts,.m2ts,.3gp,.ogv,video/*"
                  disabled={ingestBusy}
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setIngestFile(file);
                    setIngestProgress(0);
                    setIngestResult(null);
                    setIngestStatus("");
                  }}
                  className={`${field} w-full text-[9px]`}
                />
              </label>

              <label className="text-[9px]">
                <span className="block mb-1 opacity-65">DESTINATION</span>
                <select
                  value={ingestChannelId}
                  disabled={ingestBusy}
                  onChange={(e) => {
                    setIngestChannelId(e.target.value);
                    setIngestResult(null);
                  }}
                  className={`${field} w-full`}
                >
                  {activeS4Mappings.map((m) => {
                    const ch = m.channel || realChannels.find((c) => c.id === m.channel_id);
                    return (
                      <option key={m.id} value={m.channel_id}>
                        {ch ? `CH ${ch.number} — ${ch.name}` : m.remote_folder}
                      </option>
                    );
                  })}
                </select>
              </label>

              <button
                type="button"
                onClick={uploadMovieToS4}
                disabled={ingestBusy || !ingestFile || !ingestChannelId || !ingestMapping}
                className={btn}
              >
                {ingestBusy ? "WORKING..." : "UPLOAD MOVIE"}
              </button>
            </div>

            {ingestFile && (
              <div className="mt-2 border border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] p-2 text-[9px]">
                <div className="flex justify-between gap-3 flex-wrap">
                  <span>{ingestFile.name}</span>
                  <span className="opacity-60">{(ingestFile.size / (1024 * 1024)).toFixed(1)} MB</span>
                </div>
                <div className="mt-1 opacity-55 truncate">
                  S4 DESTINATION: {ingestMapping?.s4_prefix || "SELECT A CHANNEL"}{ingestFile.name}
                </div>
              </div>
            )}

            {(ingestBusy || ingestStatus) && (
              <div className="mt-2">
                <div className="flex justify-between text-[8px] opacity-65">
                  <span>{ingestStatus || "READY"}</span>
                  <span>{ingestProgress}%</span>
                </div>
                <div className="mt-1 h-2 border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)]">
                  <div
                    className="h-full bg-[var(--phosphor)] transition-[width]"
                    style={{ width: `${ingestProgress}%` }}
                  />
                </div>
              </div>
            )}

            {ingestResult?.tape && (
              <div className="mt-3 border border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] p-2 text-[9px]">
                <div className="text-[10px] tracking-widest">UPLOAD COMPLETE</div>
                <div className="mt-1">{ingestResult.tape.title}</div>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 opacity-75">
                  <span>S4 ✓</span>
                  <span>TAPE CREATED ✓</span>
                  <span>
                    TMDB {ingestResult.tape.tmdb_id
                      ? `✓ ${ingestResult.tape.tmdb_id}`
                      : ingestResult.tape.tmdb_status === "review"
                        ? "REVIEW"
                        : "—"}
                  </span>
                  <span>POSTER {ingestResult.tape.poster_url ? "✓" : "—"}</span>
                </div>
              </div>
            )}

            <div className="mt-2 text-[8px] opacity-50 leading-relaxed">
              DUPLICATE GUARDS CHECK THE EXACT S4 OBJECT, EXISTING TAPE TITLE AND HIGH-CONFIDENCE TMDB ID BEFORE UPLOAD.
            </div>
          </div>

          <div
            className="border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] p-3"
            data-testid="s4-movie-management"
          >
            <div>
              <div className="text-[11px] tracking-[0.16em] font-bold">S4 MOVIE MANAGEMENT</div>
              <div className="mt-1 text-[9px] opacity-65">
                MOVE, RENAME, REPLACE OR REMOVE A PRODUCTION S4 MOVIE. PHYSICAL STORAGE AND CATALOGUE STAY IN SYNC.
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-2">
              <label className="text-[9px]">
                <span className="block mb-1 opacity-65">CHANNEL</span>
                <select
                  value={manageChannelId}
                  disabled={manageBusy}
                  onChange={(e) => {
                    setManageChannelId(e.target.value);
                    setManageVideoId("");
                    setManageStatus("");
                  }}
                  className={`${field} w-full`}
                >
                  {activeS4Mappings.map((m) => {
                    const ch = m.channel || realChannels.find((c) => c.id === m.channel_id);
                    return (
                      <option key={m.id} value={m.channel_id}>
                        {ch ? `CH ${ch.number} — ${ch.name}` : m.remote_folder}
                      </option>
                    );
                  })}
                </select>
              </label>

              <label className="text-[9px]">
                <span className="block mb-1 opacity-65">MOVIE</span>
                <select
                  value={manageVideoId}
                  disabled={manageBusy || !manageChannelId}
                  onChange={(e) => setManageVideoId(e.target.value)}
                  className={`${field} w-full`}
                >
                  {!manageRows.length && <option value="">NO S4 MOVIES</option>}
                  {manageRows.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.title}{row.year ? ` (${row.year})` : ""}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {manageMovie && (
              <div className="mt-3 border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] p-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-[10px]">{manageMovie.title}</div>
                    <div className="mt-1 text-[8px] opacity-55 break-all">
                      s4://{manageMovie.s4_bucket}/{manageMovie.s4_object_key}
                    </div>
                    <div className="mt-1 text-[8px] opacity-55">
                      TMDB {manageMovie.tmdb_id || "—"} · ORDER {manageMovie.order ?? "—"}
                    </div>
                  </div>
                  {manageMovie.poster_url && (
                    <img
                      src={manageMovie.poster_url}
                      alt=""
                      className="w-10 h-14 object-cover border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)]"
                    />
                  )}
                </div>

                <div className="mt-3 grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-2 items-end">
                  <label className="text-[9px]">
                    <span className="block mb-1 opacity-65">RENAME S4 FILE + TAPE TITLE</span>
                    <input
                      value={manageRename}
                      disabled={manageBusy}
                      onChange={(e) => setManageRename(e.target.value)}
                      className={`${field} w-full`}
                    />
                  </label>
                  <button
                    type="button"
                    className={btn}
                    disabled={manageBusy || !manageRename.trim()}
                    onClick={renameManagedMovie}
                  >
                    RENAME
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-2 items-end">
                  <label className="text-[9px]">
                    <span className="block mb-1 opacity-65">MOVE TO CHANNEL</span>
                    <select
                      value={manageMoveChannelId}
                      disabled={manageBusy}
                      onChange={(e) => setManageMoveChannelId(e.target.value)}
                      className={`${field} w-full`}
                    >
                      <option value="">SELECT DESTINATION</option>
                      {activeS4Mappings
                        .filter((m) => m.channel_id !== manageChannelId)
                        .map((m) => {
                          const ch = m.channel || realChannels.find((c) => c.id === m.channel_id);
                          return (
                            <option key={m.id} value={m.channel_id}>
                              {ch ? `CH ${ch.number} — ${ch.name}` : m.remote_folder}
                            </option>
                          );
                        })}
                    </select>
                  </label>
                  <button
                    type="button"
                    className={btn}
                    disabled={manageBusy || !manageMoveChannelId}
                    onClick={moveManagedMovie}
                  >
                    MOVE
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-2 items-end">
                  <label className="text-[9px]">
                    <span className="block mb-1 opacity-65">REPLACE VIDEO FILE</span>
                    <input
                      type="file"
                      accept=".mp4,.mkv,.avi,.mov,.webm,.m4v,.wmv,.flv,.mpg,.mpeg,.ts,.m2ts,.3gp,.ogv,video/*"
                      disabled={manageBusy}
                      onChange={(e) => {
                        setReplaceFile(e.target.files?.[0] || null);
                        setReplaceProgress(0);
                        setManageStatus("");
                      }}
                      className={`${field} w-full text-[9px]`}
                    />
                  </label>
                  <button
                    type="button"
                    className={btn}
                    disabled={manageBusy || !replaceFile}
                    onClick={replaceManagedMovie}
                  >
                    REPLACE
                  </button>
                </div>

                {replaceProgress > 0 && (
                  <div className="mt-2">
                    <div className="flex justify-between text-[8px] opacity-65">
                      <span>{manageStatus || "REPLACING"}</span>
                      <span>{replaceProgress}%</span>
                    </div>
                    <div className="mt-1 h-2 border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)]">
                      <div
                        className="h-full bg-[var(--phosphor)] transition-[width]"
                        style={{ width: `${replaceProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="mt-3 pt-3 border-t border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-[8px] opacity-55">
                    REMOVE DELETES THE PHYSICAL S4 OBJECT AND SOFT-DELETES THE CATALOGUE TAPE.
                  </div>
                  <button
                    type="button"
                    disabled={manageBusy}
                    onClick={deleteManagedMovie}
                    className="border border-red-500/70 text-red-400 px-2 py-1 hover:bg-red-500 hover:text-black transition-colors disabled:opacity-40"
                  >
                    REMOVE MOVIE
                  </button>
                </div>

                {manageStatus && replaceProgress === 0 && (
                  <div className="mt-2 text-[8px] opacity-65">{manageStatus}</div>
                )}
              </div>
            )}

            <div className="mt-2 text-[8px] opacity-50 leading-relaxed">
              MOVE AND RENAME COPY + VERIFY THE NEW S4 OBJECT BEFORE THE CATALOGUE POINTER CHANGES, THEN DELETE + VERIFY THE OLD OBJECT.
              REPLACE PRESERVES THE TAPE ID, TMDB METADATA AND PLAYLIST POSITION.
            </div>
          </div>

          {reviewOpen && (
            <div className="border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] p-2" data-testid="mega-sync-review-queue">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] tracking-widest">TMDB REVIEW QUEUE</div>
                  <div className="mt-1 text-[8px] opacity-60">
                    AMBIGUOUS AUTO-MATCHES ONLY. APPROVAL UPDATES POSTER, DESCRIPTION, YEAR AND TMDB LINKAGE.
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => loadReviewQueue(false)} className={btn}>REFRESH</button>
                  <button type="button" onClick={() => setReviewOpen(false)} className={btn}>CLOSE</button>
                </div>
              </div>

              <div className="mt-2 space-y-2">
                {!reviewItems.length && (
                  <div className="text-[10px] opacity-60 py-3">NO TMDB ITEMS NEED REVIEW.</div>
                )}

                {reviewItems.map((item) => {
                  const key = reviewKey(item);
                  const candidates = reviewCandidates[key] || [];
                  const busyItem = reviewBusyKey === key;
                  const title = item.video?.title || item.video_title || "Untitled";
                  const channel = item.channel;
                  return (
                    <div key={key} className="border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] p-2">
                      <div className="flex gap-3 items-start">
                        {item.poster_url ? (
                          <img src={item.poster_url} alt="" className="w-[54px] h-[80px] object-cover border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)]" />
                        ) : (
                          <div className="w-[54px] h-[80px] border border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] flex items-center justify-center text-[8px] opacity-40">NO ART</div>
                        )}

                        <div className="min-w-0 flex-1">
                          <div className="text-[11px]">{title}</div>
                          <div className="text-[8px] opacity-55 truncate">{item.remote_path}</div>
                          <div className="text-[8px] opacity-65 mt-1">
                            {channel ? `CH ${channel.number} — ${channel.name}` : "CHANNEL UNKNOWN"}
                            {" · "}
                            AUTO SUGGESTION: {item.suggested_title || "NONE"}{item.suggested_year ? ` (${item.suggested_year})` : ""}
                            {" · "}
                            CONFIDENCE {Math.round((item.confidence || 0) * 100)}%
                          </div>

                          <div className="mt-2 flex gap-2 flex-wrap">
                            <input
                              value={reviewQueries[key] ?? item.video_title ?? title}
                              onChange={(e) => setReviewQueries((prev) => ({ ...prev, [key]: e.target.value }))}
                              className={`${field} flex-1 min-w-[220px]`}
                              placeholder="Search TMDb"
                            />
                            <button type="button" onClick={() => searchReviewCandidates(item)} disabled={busyItem} className={btn}>
                              FIND MATCHES
                            </button>
                            <button type="button" onClick={() => dismissReviewItem(item)} disabled={busyItem} className={btn}>
                              LEAVE UNMATCHED
                            </button>
                          </div>
                        </div>
                      </div>

                      {!!candidates.length && (
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
                          {candidates.map((candidate) => (
                            <div key={`${candidate.media_type}-${candidate.tmdb_id}`} className="border border-[color-mix(in_srgb,var(--phosphor)_20%,transparent)] p-2 flex gap-2">
                              {candidate.poster_thumb_url ? (
                                <img src={candidate.poster_thumb_url} alt="" className="w-[46px] h-[68px] object-cover shrink-0" />
                              ) : (
                                <div className="w-[46px] h-[68px] border border-[color-mix(in_srgb,var(--phosphor)_18%,transparent)] shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="text-[9px] leading-tight">{candidate.title}</div>
                                <div className="text-[8px] opacity-55 mt-1">
                                  {candidate.year || "YEAR ?"} · {String(candidate.media_type || "").toUpperCase()}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => applyReviewCandidate(item, candidate)}
                                  disabled={busyItem}
                                  className={`${btn} mt-2 text-[8px]`}
                                >
                                  USE THIS MATCH
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Phase 2.3.65.1-r1.1
              Mapping creation, bulk mapping and per-map scan/remove controls are
              intentionally no longer exposed. Unified automatic media ingest owns
              production S4 mappings. Existing mappings remain untouched. */}
          {status && !status.logged_in && (
            <div className="border border-yellow-500/50 p-2 text-[9px] leading-relaxed">
              {status.detail}. INSTALL/LOGIN TO MEGACMD ON THE VPS AS THE SAME USER RUNNING
              SCARTLEAD-TV. THE MEGACMD SESSION IS PERSISTED BY MEGA; NO PASSWORD IS STORED IN THIS APP.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
